import React, {useState, useEffect} from 'react';
import {useAuth} from '../context/AuthContext';
import {Link} from "react-router-dom";
import Navigation from '../components/Navigation';
import { getPackageWarnings } from '../utils/daycareUtils';
import '../styles/pages/daycare.css';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

// Extract YYYY-MM-DD from any stored date using UTC (avoids local-offset shifting)
const toDateInput = (val) => {
    if (!val) return '';
    return new Date(val).toISOString().split('T')[0];
};

// Format a date as DD-MM-YYYY for display
const toDisplayDate = (val) => {
    if (!val) return '';
    const [y, m, d] = new Date(val).toISOString().split('T')[0].split('-');
    return `${d}-${m}-${y}`;
};

// Parse a YYYY-MM-DD string as noon local time so it survives UTC conversion intact
const fromDateInput = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0).toISOString();
};

export default function ActivePackages() {
    const { user, role, logout, authorizedFetch} = useAuth();
    const [packages, setPackages] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editingPackage, setEditingPackage] = useState(null);
    const [editForm, setEditForm] = useState({
        packageType: '', daysLeft: 0,
        purchasedAt: '', firstUsedAt: '', expiresAt: ''
    });

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const PER_PAGE = 12;

    // Load active packages
    const loadActivePackages = async () => {
        try {
            // Archive any packages that passed their expiry date
            await authorizedFetch(`${API_URL}/api/daycare/packages/expire-stale`, { method: 'POST' }).catch(() => {});

            const res = await authorizedFetch(`${API_URL}/api/daycare/packages/active`);
            if (!res.ok) throw new Error(await res.text());
            const packagesData = await res.json();
            setPackages(packagesData);
        } catch (err) {
            console.error('🔴 Failed to load active packages:', err);
            setError(err.message);
        }
    };

    useEffect(() => {
        if (!user) return;
        loadActivePackages();
    }, [user]);

    // Handle edit
    const handleEdit = (pkg) => {
        setEditingPackage(pkg._id);
        setEditForm({
            packageType: pkg.packageType,
            daysLeft: pkg.daysLeft,
            purchasedAt: toDateInput(pkg.purchasedAt),
            firstUsedAt: toDateInput(pkg.firstUsedAt),
            expiresAt:   toDateInput(pkg.expiresAt),
        });
    };

    // Handle edit save
    const handleEditSave = async (id) => {
        try {
            const payload = {
                packageType: editForm.packageType,
                daysLeft: editForm.daysLeft,
                purchasedAt: fromDateInput(editForm.purchasedAt),
                firstUsedAt: fromDateInput(editForm.firstUsedAt),
                expiresAt:   fromDateInput(editForm.expiresAt),
            };
            const res = await authorizedFetch(`${API_URL}/api/daycare/packages/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(await res.text());

            setSuccess('¡Paquete actualizado con éxito!');
            setEditingPackage(null);
            loadActivePackages();
        } catch (err) {
            console.error('Error updating package:', err);
            setError(err.message || 'No se pudo actualizar el paquete');
        }
    };

    // Handle edit cancel
    const handleEditCancel = () => {
        setEditingPackage(null);
        setEditForm({ packageType: '', daysLeft: 0, purchasedAt: '', firstUsedAt: '', expiresAt: '' });
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            const res = await authorizedFetch(`${API_URL}/api/daycare/packages/${id}`, {
                method: 'DELETE',
            });
            if (!res.ok) throw new Error('Failed to delete package');
            // Refresh the packages list after deletion
            setPackages(packages.filter((pkg) => pkg._id !== id));
        } catch (err) {
            console.error('Error deleting package:', err);
            setError('No se pudo eliminar el paquete');
        }
    };

    const valid = packages.filter(p => p.dog && p.dog.name);

    const filtered = search.trim()
        ? valid.filter(p => p.dog.name.toLowerCase().includes(search.trim().toLowerCase()))
        : valid;

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    const handleSearch = (val) => {
        setSearch(val);
        setPage(1);
    };

    return (
        <div>
            <Navigation />
            <div className="daycare-container">

            <div className="daycare-header">
                <h1>Paquetes de Guardería Activos</h1>
                <Link to="/daycare/packages" className="daycare-link">← Volver a paquetes</Link>
            </div>

            {error && <div className="message error">{error}</div>}
            {success && <div className="message success">{success}</div>}

            {/* Search bar */}
            <div className="daycare-search-row">
                <input
                    className="daycare-search-input"
                    type="text"
                    placeholder="Buscar por nombre de perro..."
                    value={search}
                    onChange={e => handleSearch(e.target.value)}
                />
                {search && (
                    <button className="daycare-search-clear" onClick={() => handleSearch('')}>✕</button>
                )}
            </div>

            {/* Edit Form Modal (appears when editing) */}
            {editingPackage && (
                <div className="daycare-form">
                    <h2>Editar paquete</h2>

                    <label>Tipo de paquete:</label>
                    <select
                        value={editForm.packageType}
                        onChange={(e) => setEditForm({...editForm, packageType: e.target.value})}
                    >
                        <option value="1-day">1 Día</option>
                        <option value="15-day">15 Días</option>
                        <option value="30-day">30 Días</option>
                    </select>

                    <label>Días restantes:</label>
                    <input
                        type="number"
                        value={editForm.daysLeft}
                        onChange={(e) => setEditForm({...editForm, daysLeft: parseInt(e.target.value) || 0})}
                        min="0"
                    />

                    <label>Fecha de compra:</label>
                    <input
                        type="date"
                        value={editForm.purchasedAt}
                        onChange={(e) => setEditForm({...editForm, purchasedAt: e.target.value})}
                    />

                    <label>Fecha de primer uso <span style={{fontWeight: 400, fontSize: '0.8rem'}}>(inicio de vigencia)</span>:</label>
                    <input
                        type="date"
                        value={editForm.firstUsedAt}
                        onChange={(e) => setEditForm({...editForm, firstUsedAt: e.target.value})}
                    />

                    <label>Fecha de vencimiento:</label>
                    <input
                        type="date"
                        value={editForm.expiresAt}
                        onChange={(e) => setEditForm({...editForm, expiresAt: e.target.value})}
                    />

                    <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                        <button onClick={() => handleEditSave(editingPackage)}>Guardar</button>
                        <button onClick={handleEditCancel} style={{backgroundColor: '#6b7280'}}>Cancelar</button>
                    </div>
                </div>
            )}

            {valid.length === 0 ? (
                <p className="no-data">No hay paquetes activos.</p>
            ) : filtered.length === 0 ? (
                <p className="no-data">No se encontraron paquetes para "{search}".</p>
            ) : (
                <div className="daycare-list">
                    {paginated.map((p) => {
                        const pkgWarnings = getPackageWarnings(p);
                        return (
                        <div key={p._id} className="daycare-card">
                            <h3>{p.dog?.name || 'Perro desconocido'}</h3>
                            {pkgWarnings.length > 0 && (
                                <div className="pkg-warnings">
                                    {pkgWarnings.map((w, i) => (
                                        <div key={i} className={`pkg-warning pkg-warning--${w.type}`}>
                                            ⚠️ {w.message}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p>Paquete: {p.packageType}</p>
                            <p>Días restantes: {p.daysLeft}</p>
                            <p>Vence: {p.expiresAt ? toDisplayDate(p.expiresAt) : 'Se establece en primer uso'}</p>

                            <div className="actions">
                                <button
                                    className="edit-btn"
                                    onClick={() => handleEdit(p)}
                                    disabled={editingPackage === p._id}
                                    style={{ background: '#3b82f6', color: 'white' }}
                                >
                                    Editar
                                </button>
                                <button
                                    className="delete-btn"
                                    onClick={() => handleDelete(p._id)}
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {totalPages > 1 && (
                <div className="daycare-pagination">
                    <button
                        className="daycare-page-btn"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                    >
                        ← Anterior
                    </button>
                    <span className="daycare-page-info">Página {page} de {totalPages}</span>
                    <button
                        className="daycare-page-btn"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                    >
                        Siguiente →
                    </button>
                </div>
            )}
            </div>
        </div>
    );
}
